import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { getFilterFromLocalStorage, setFilterToLocalStorage } from "utils/common";
import { ENTITY_FILTER_TYPES, ENTITY_COLUMNS_TYPES } from "utils/common";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import { FiChevronsDown, FiFilter } from "react-icons/fi";
import CustomTable from "components/table/CustomTable";
import { USER_USAGE_API } from "constants/endpoints";
import { userUsageColumns } from "./variables";
import { formatDateToMMDDYYYY } from "utils/common";
import { FEATURES_LISTING_API } from "constants/endpoints";
import { useLocation } from "react-router-dom/"
import { useQueryParams } from "utils/common";
import { getUniquePlanDropdownData } from "constants/commonFunctions";
import { useAuth } from "contexts/AuthContext";
import { PLAN_USAGE_KEY } from "constants/filterDropdownData";
import { PLAN_USAGE_REF_TYPE } from "constants/filterDropdownData";

const PAGE_SIZE = 50;
const sortableColumns = ["created_at", "updated_at"];
const today = new Date();
const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(today.getDate() - 30);

export default function UserUsage() {
  const [userUsageData, setUserUsageData] = useState([]);
  const { apiCall, loading = true, error } = useApi();
  const [isOpenFilterSidebar, setIsOpenFilterSidebar] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const lsFilters = getFilterFromLocalStorage(ENTITY_FILTER_TYPES.USER_USAGE_FILTER);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAction, setSelectedAction] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const location = useLocation();
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const lsUsersDateRange = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.USER_USAGE_DATE_RANGE
  )
  const { subId } = useQueryParams();
  const initialFiltersData = {
    plan: [],
    subscription_id: "",
    key: "",
    ref_type: "",
    dateRange: {
      startDate: thirtyDaysAgo,
      endDate: today,
    },
  };

  const [filters, setFilters] = useState(lsFilters ? lsFilters : { ...initialFiltersData });
  const { plans} = useAuth();
  const PLAN_DROPDOWN_DATA = getUniquePlanDropdownData(plans);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchTextValue = params.get("searchText");

    if (searchTextValue) {
      setSearchText(searchTextValue);
      fetchUserUsageData(searchTextValue)
    }
  }, [location]);

  const {
    plan = [],
    subscription_id = subId,
    key = "",
    ref_type,
    dateRange: { startDate = "", endDate = "" } = {},
  } = filters || {};

  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    sortType: "created_at",
    sortDirection: "descending",
  });

  const fetchUserUsageData = useCallback(async (providedUserText) => {
    try {
      const body = {
        search_text: providedUserText || searchText,
        page: currentPage,
        limit: pageSize,
        start_date: formatDateToMMDDYYYY(startDate),
        end_date: formatDateToMMDDYYYY(endDate),
        plan: plan.join(','),
        subscription_id: subId || subscription_id,
        key,
        ref_type
      };
      const res = await apiCall.get(`${USER_USAGE_API}`, body);
      setUserUsageData(res?.data?.usages || []);
      setTotalPages(Math.ceil(res?.data?.count / pageSize));
      setTotalCount(res?.data?.count || 0);
    } catch (error) {
      console.error("Error while fetching user usage:", error.message);
    }
  }, [currentPage, pageSize, searchText, filters, subId]);



  const toggleSidebar = () => {
    setIsOpenFilterSidebar(!isOpenFilterSidebar);
  };

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters };
      newFilters[key] = value;
      return newFilters;
    });
    setCurrentPage(1);
  }, []);

  const handleApplyFilter = () => {
    const applyFilterArray = Object.keys(filters).filter((key) => filters[key]);
    setFilteredData(applyFilterArray);
    const filtersWithDateRange = {
      ...filters,
      dateRange: {
        startDate: startDate,
        endDate: endDate,
      },
    };
    setFilterToLocalStorage(ENTITY_FILTER_TYPES.USER_USAGE_FILTER, filtersWithDateRange);
    setCurrentPage(1);
    fetchUserUsageData();
  };

  const handleRemoveAllFilter = () => {
    setFilteredData([]);
    setFilters({
      ...initialFiltersData,
      dateRange: {
        startDate: "",
        endDate: "",
      },
    });

  };

  const dropdownFilters = [
    {
      name: 'plan',
      data: PLAN_DROPDOWN_DATA,
      placeholder: 'Select Plan',
      value: plan,
    },
    {
      name: "key",
      placeholder: "Key",
      value: filters?.key,
      data: PLAN_USAGE_KEY,
    },
    {
      name: "ref_type",
      placeholder: "Ref Type",
      value: filters?.ref_type,
      data: PLAN_USAGE_REF_TYPE,
    },
  ];

  const columns = useMemo(() => userUsageColumns, [userUsageColumns]);
  const data = useMemo(() => userUsageData, [userUsageData]);
  const excludedColumnVisibility = ["user_email"];
  const [columnVisibility, setColumnVisibility] = useState(
    columns.reduce((acc, column) => {
      acc[column.accessor] = !excludedColumnVisibility.includes(column.accessor);
      return acc;
    }, {})
  );

  const [manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox, setManageColumnSelectAllCheckbox] = useState(false);

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleRowSelect = useCallback((row) => {
    const findData = selectedRowsData.find((item) => item._id === row._id);
    setSelectedRowsData((prev) => {
      if (findData) {
        return prev.filter((item) => item._id !== findData._id);
      } else {
        return [...prev, row];
      }
    });
  }, [selectedRowsData]);


  useEffect(() => {
    setFilterToLocalStorage(ENTITY_COLUMNS_TYPES.USER_USAGE_COLUMNS, columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    fetchUserUsageData();
  }, [currentPage, pageSize, sortConfig]);

  const visibleColumns = columns.filter((column) => columnVisibility[column.accessor]);

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUserUsageData();
  };

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <Box height="100vh" bg="#F8FAFB">
      <Flex
        justify="space-between"
        align="center"
        p="2"
        boxShadow="sm"
        borderRadius="sm"
        pos="fixed"
        style={{ width: "calc(100vw - 96px)" }}
        zIndex={1000}
      >
        <HStack spacing="4" p={2}>
          <Box position="relative" display="inline-block">
            <Button
              paddingX={6}
              leftIcon={<FiFilter />}
              rightIcon={<FiChevronsDown />}
              colorScheme="teal"
              variant="outline"
              borderRadius="md"
              size="sm"
              onClick={toggleSidebar}
            >
              Filter
            </Button>
            {filteredData.length > 0 && (
              <Badge
                position="absolute"
                top="-3"
                right="0"
                colorScheme="teal"
                borderRadius="full"
                px={2}
                fontSize="0.8em"
              >
                {filteredData.length}
              </Badge>
            )}
          </Box>
          <ManageColumns
            columns={columns}
            handleColumnChange={handleColumnChange}
            columnVisibility={columnVisibility}
            setManageColumnSelectAllCheckbox={setManageColumnSelectAllCheckbox}
            manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox}
          />
          <form onSubmit={handleInputFormSubmit}>
            <InputGroup size="sm">
              <SearchBar
                background="#F9F9FB"
                color="#637381"
                setSearchText={(e) => setSearchText(e.target.value)}
                searchText={searchText}
                borderRadius="5px"
                borderWidth="0.5px"
                borderColor="#637381"
              />
            </InputGroup>
          </form>
        </HStack>
        <Text
          borderRadius="5px"
          borderWidth="0.5px"
          borderColor="#637381"
          background="#F9F9FB"
          color="#637381"
          p={1}
        >
          Total User Usage: {totalCount}
        </Text>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(value) => {
            setCurrentPage(value);
          }}
          totalCount={totalCount}
          setCurrentPage={setCurrentPage}
          setPageSize={setPageSize}
          pageSize={pageSize}
          setPageSizeToLocalStorage={(value) =>
            setFilterToLocalStorage(
              ENTITY_FILTER_TYPES.USER_USAGE_PAGE_SIZE,
              value
            )
          }
        />
      </Flex>
      <SidebarFilter
        filters={{ dropdowns: dropdownFilters }}
        handleFilterChange={handleFilterChange}
        handleApplyFilter={handleApplyFilter}
        isOpen={isOpenFilterSidebar}
        onClose={toggleSidebar}
        appliedFilters={filters}
        lsFilters={lsFilters}
        setFilters={setFilters}
        multiSelectKey={["plan"]}
        isCalenderFilter={true}
        enableMultiselect={true}
        handleRemoveAllFilter={handleRemoveAllFilter}
      />
      {userUsageData.length !== 0 ? (
        <CustomTable
          columns={visibleColumns}
          data={data}
          onSelectedAction={setSelectedAction}
          selectedRowsData={selectedRowsData}
          handleRowSelect={handleRowSelect}
          setSelectedRowsData={setSelectedRowsData}
          sortableColumns={sortableColumns}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
        />
      ) : !loading ? (
        <NoDataFound />
      ) : null}
      <OverlayLoaderComponent isOpen={loading} />
    </Box>
  );
}
